import type { AxiosError } from 'axios';

/** Fixed copy for HTTP 404 — rules-CRUD 404s have empty bodies, so the body is ignored. */
export const NOT_FOUND_MESSAGE = 'The requested item was not found.';

/**
 * Tolerant extraction of a ProblemDetails-style reason from an axios error
 * body. Handles the three backend shapes (verified in research):
 *  - raw ProblemDetails:            `{ detail: "reason" }`
 *  - wrapped ProblemDetails:        `{ success:false, data:{ detail:"reason" }, errors:[title, reason] }`
 *  - string-array envelope errors:  `{ success:false, errors:[title, reason] }`
 * Read order: `detail ?? data?.detail ?? errors?.[1] ?? errors?.[0]`
 * (errors[0] is the ProblemDetails title in the wrapped shape; errors[1] is
 * the reason). Returns undefined for empty/non-object bodies.
 */
export function getProblemDetail(err: unknown): string | undefined {
  const body = (err as AxiosError | null | undefined)?.response?.data as unknown;
  if (typeof body !== 'object' || body === null) return undefined;

  const { detail, data, errors } = body as {
    detail?: unknown;
    data?: { detail?: unknown } | null;
    errors?: unknown;
  };

  return (
    asNonEmptyString(detail) ??
    asNonEmptyString(data?.detail) ??
    (Array.isArray(errors)
      ? asNonEmptyString(errors[1]) ?? asNonEmptyString(errors[0])
      : undefined)
  );
}

/**
 * Maps an axios error to a display-friendly message:
 *  - 404 → fixed not-found copy (body ignored; rules-CRUD 404s have empty bodies)
 *  - 422 → the server's ProblemDetails reason verbatim (rule-block reason or
 *    validation message), falling back when the body carries none
 *  - 403 → generic permission copy (team-action 403s have empty bodies)
 *  - anything else → the caller-supplied fallback
 * Never surfaces raw `err.message` stacks.
 */
export function axiosErrorMessage(err: unknown, fallback: string): string {
  const status = (err as AxiosError | null | undefined)?.response?.status;
  if (status === 404) return NOT_FOUND_MESSAGE;
  if (status === 422) return getProblemDetail(err) ?? fallback;
  if (status === 403) return "You don't have permission to perform this action.";
  return fallback;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
